import { getCore } from '../../../../client/core/getCore';
import type { DynamicClient } from '../../../../client/types';
import { assertDefined } from '../../../../utils/assertDefined';
import { getNonce } from '../../../../utils/getNonce';
import { CHAINS_INFO_MAP } from '../../constants';
import type { WalletAccount } from '../../walletAccount';
import { formatSignInMessage } from '../formatSignInMessage';
import { getChainIdForAccountVerification } from '../getChainIdForAccountVerification';
import { getWalletProviderFromWalletAccount } from '../getWalletProviderFromWalletAccount';
import { createSignInMessageStatement } from './createSignInMessageStatement';

type GetSignInMessageParams = {
  walletAccount: WalletAccount;
};

export const getSignInMessage = async (
  { walletAccount }: GetSignInMessageParams,
  client: DynamicClient
) => {
  const core = getCore(client);
  const universalLink = core.metadata?.universalLink;

  assertDefined(universalLink, 'Universal link is not set');

  const url = new URL(universalLink);
  const domain = url.host;

  const nonce = await getNonce(client);

  const walletProvider = getWalletProviderFromWalletAccount(
    {
      walletAccount,
    },
    client
  );

  // for starknet, the message to sign needs to be a JSON object with the domain and nonce
  if (walletProvider.chain === 'STARK') {
    /**
     * Starknet requires the strings to be 31 chars long
     * Nonce - we need to keep full size for nonce verification on the backend (the backend shortens it to 31 chars)
     * Domain - we need to shorten the domain to 31 chars
     */
    return JSON.stringify({
      domain: domain.substring(0, 31),
      nonce,
    });
  }

  const statement = createSignInMessageStatement(client);

  const messageToSign = await formatSignInMessage({
    address: walletAccount.address,
    blockchainName: CHAINS_INFO_MAP[walletProvider.chain].blockchainName,
    chainId: await getChainIdForAccountVerification({ walletProvider }),
    domain,
    issuedAt: new Date().toISOString(),
    nonce,
    requestId: core.environmentId,
    statement,
    uri: url.toString(),
  });

  return messageToSign;
};
